import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Row, Col, Card, Collapse, Form, Container } from 'react-bootstrap';
import { SingleSelectAsync, SelectOption } from './SIngleSelectAsync';
import HeaderComponent from './HeaderComponent';
import FeatureNav from './FeatureNav';
import BreadCrumbs from './BreadCrumbs';
import Loader from './Loader';
import { prodApiEndpointUrl, stagingApiEndpointUrl } from 'types/apiEndpoints';
import { Tabulator, PageModule, DownloadModule, ExportModule, SortModule } from "tabulator-tables";
import "tabulator-tables/dist/css/tabulator.min.css";
import * as d3 from "d3";
import { MultiSelectAsync } from './MultiSelectAsync';
import FooterSection from './FooterSection';

Tabulator.registerModule([PageModule, DownloadModule, ExportModule, SortModule]);

interface Entity {
  entity_id: number;
  entity_name: string;
}

interface SharedJobData {
  entity_name: string;
  fiscal_year: number;
  wages: number;
  benefits: number;
  total: number;
  title: string;
  employee_name: string;
  govt_lvl: string;
}

interface JobTitleShared {
  title: string;
}

interface Summary {
	observations?: number;
	mean?: number;
	standard_deviation?: number;
	total_sum?: number;
}

interface Percentiles {
	minimum?: number;
	twenty_fifth_percentile?: number;
	median?: number;
	seventy_fifth_percentile?: number;
	maximum?: number;
}

interface StatisticsCardProps {
	title: string;
	value?: number;
	bg?: "secondary" | "primary" | "primary-variation" | "secondary-variation";
	isCurrency?: boolean;
}

const EntityCompareTwoEntities = () => {
  const [showHeaderInfo, setShowHeaderInfo] = useState<boolean>(false);
  const [masterData, setMasterData] = useState<SharedJobData[] | null>(null);
  const [selectedEntityName1, setSelectedEntityName1] = useState<string | null>(null);
  const [selectedEntityName2, setSelectedEntityName2] = useState<string | null>(null);
  const [selectedEntityId1, setSelectedEntityId1] = useState<string | null>(null);
  const [selectedEntityId2, setSelectedEntityId2] = useState<string | null>(null);
	const [searchOptionType, setSearchOptionType] = useState<"Shared Titles" | "Shared Partial Titles" | "Custom Search" | null>("Shared Titles");
  const [selectedEmployeeNames, setSelectedEmployeeNames] = useState<string[] | null>(null);
  const [allEntities, setAllEntities] = useState<Entity[] | null>(null);
  const [allSharedJobTitles, setAllSharedJobTitles] = useState<JobTitleShared [] | null>(null);
	const [allSharedPartialJobTitles, setAllSharedPartialJobTitles] = useState<JobTitleShared [] | null>(null);
  const [allEmployeeNames, setAllEmployeeNames] = useState<string[] | null>(null);
  const [allEmployeeTitles, setAllEmployeeTitles] = useState<string[] | null>(null);
  const [allFiscalYears, setAllFiscalYears] = useState<number[] | null>(null);
  const [selectedEmployeeTitles, setSelectedEmployeeTitles] = useState<string[] | null> (null);
  const [selectedFiscalYears, setSelectedFiscalYears] = useState<number[] | null>(null);
  const [selectedCommonTitle, setSelectedCommonTitle] = useState<string | null> (null);
	const [filteredData, setFilteredData] = useState<SharedJobData[] | null>(null);
	const [entity1Data, setEntity1Data] = useState<SharedJobData[] | null>(null);
	const [entity2Data, setEntity2Data] = useState<SharedJobData[] | null>(null);
	const [entity1SummaryData, setEntity1SummaryData] = useState<Summary | null>(null);
	const [entity2SummaryData, setEntity2SummaryData] = useState<Summary | null>(null);
	const [entity1PercentileData, setEntity1PercentileData] = useState<Percentiles | null>(null);
	const [entity2PercentileData, setEntity2PercentileData] = useState<Percentiles | null>(null);
	const [entity1FilteredSummaryData, setEntity1FilteredSummaryData] = useState<Summary | null>(null);
	const [entity2FilteredSummaryData, setEntity2FilteredSummaryData] = useState<Summary | null>(null);
	const [entity1FilteredPercentileData, setEntity1FilteredPercentileData] = useState<Percentiles | null>(null);
	const [entity2FilteredPercentileData, setEntity2FilteredPercentileData] = useState<Percentiles | null>(null);
  const [apiEndpoint] = useState<string>(() => {
    return window.location.hostname === "transparent.utah.gov" ? prodApiEndpointUrl : stagingApiEndpointUrl;
  });
  const chartRef = useRef<SVGSVGElement>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tabulatorRef = useRef<InstanceType<typeof Tabulator> | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
	const [chartType, setChartType] = useState<"Total Compensation" | "Wages Only" | "Benefits Only" | null>("Total Compensation");
  const [minimumRangeSalary, setMinimumRangeSalary] = useState<number> (0);
  const [maximumRangeSalary, setMaximumRangeSalary] = useState<number> (0);
  const [numberOfBins, setNumberOfBins] = useState<number> (67);
  const [customSearchText, setCustomSearchText] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [rangeFilterFloor, setRangeFilterFloor] = useState<number>(0);
  const [rangeFilterCeiling, setRangeFilterCeiling] = useState<number>(0);
	const [noCommonTitles, setNoCommonTitles] = useState<boolean>(false);
	const [noCustomSearchResults, setNoCustomSearchResults] = useState<boolean>(false);
	const [filtersApplied, setFilterApplied] = useState<boolean>(false);

  const handleDownload = (type: "csv" | "xlsx" | "html" | "json") => {  
    const formattedEntityName1 = selectedEntityName1?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    const formattedEntityName2 = selectedEntityName2?.toLowerCase()?.replaceAll(" ", "_") || "Entity";
    if (!tabulatorRef.current) return;
    tabulatorRef.current.download(type, `${formattedEntityName1}_${formattedEntityName2}_comparison.${type}`);
  };

	const formatStringForD3Download = (string: string) => {
		if (!string || string.length === 0) return "entity";
		return string.toLowerCase().replaceAll(" ", "_");
	};

	const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const currentTitle = searchOptionType === "Custom Search" 
        ? customSearchText 
        : selectedCommonTitle;

		if (searchOptionType === "Custom Search") {
			setSelectedCommonTitle(currentTitle);
		}

    if (!selectedEntityId1 || !selectedEntityId2 || !currentTitle) {
        return;
    }

    getCommonJobTitleData(currentTitle);
	};

  const memoizedEntityNameOptions = useMemo(() => 
  (allEntities || []).map(entity => ({ 
    value: entity.entity_name.toString(), 
    label: entity.entity_name.toString(), 
  })), 
  [allEntities]);

  const loadEntityOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    if (!inputValue) return memoizedEntityNameOptions;

    const search = inputValue.toLowerCase();
    return memoizedEntityNameOptions.filter(option =>
      option.label.toLowerCase().includes(search)
    );
  }, [memoizedEntityNameOptions]);

  const memoizedSharedJobTitleOptions = useMemo(() => 
    (allSharedJobTitles || []).map(sharedJob => ({
      value: sharedJob.title,
      label: sharedJob.title,
    })), [allSharedJobTitles]);

  const loadJobTitleSharedOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    if (!inputValue) return memoizedSharedJobTitleOptions;
    
    const search = inputValue.toLowerCase();
    return memoizedSharedJobTitleOptions.filter(option =>
      option.label.toLowerCase().includes(search)
    );
  }, [memoizedSharedJobTitleOptions]);

	// Added for Shared Partial Job Titles
	const memoizedSharedPartialJobTitleOptions = useMemo(() => 
  (allSharedPartialJobTitles || []).map(sharedJob => ({
    value: sharedJob.title,
    label: sharedJob.title,
  })), [allSharedPartialJobTitles]);

	// Added for Shared Partial Job Titles
	const loadJobTitleSharedPartialOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    if (!inputValue) return memoizedSharedPartialJobTitleOptions;
    
    const search = inputValue.toLowerCase();
    return memoizedSharedPartialJobTitleOptions.filter(option =>
      option.label.toLowerCase().includes(search)
    );
  }, [memoizedSharedPartialJobTitleOptions]);

  const buildApiUrl = (apiEndpointUrl: string, searchParameter: string) => {
    return `${apiEndpointUrl}${searchParameter}`;
  };

	const StatisticsCard: React.FC<StatisticsCardProps> = ({ title, value, bg = "secondary", isCurrency = true }) => {
		const formattedValue = value !== undefined && value !== null 
			? (isCurrency ? `$${value.toLocaleString()}` : value.toLocaleString()) 
			: (isCurrency ? "$0" : "0");

		return (
			<Col xs={6} md={3} lg={true} className="mb-4">
	      <Card className={`h-100 bg-${bg} ${bg.includes("secondary") ? "text-white" : ""}`}>
	        <Card.Body className="text-center d-flex flex-column justify-content-center p-2">
	          <Card.Title className="fs-6 mb-2">{formattedValue}</Card.Title>
	          <Card.Text className="small text-uppercase fw-bold" style={{ fontSize: '0.75rem', opacity: 0.8 }}>
	            {title}
	          </Card.Text>
	        </Card.Body>
	      </Card>
	    </Col>
		);
	};

	const getAllEntities = async () => {
		setLoading(true);
		const controller = new AbortController();
		const signal = controller.signal;
		
		try {
			const getAllEntitiesSearchString = `?function=getJobTitleEntityAvailable&parameter=%7B%7D`;
			const res = await fetch(buildApiUrl(apiEndpoint, getAllEntitiesSearchString), { signal });
			const entities = res.ok ? await res.json(): [];

			const selectFilteredEntities = entities.filter((entity: any) => !entity.entity_name.includes("-- Select --"));

			setAllEntities(selectFilteredEntities);
		} catch (error) {
			console.error("Error in the getAllEntities method");
		} finally {
			setLoading(false);
		}
	}

	const getCommonJobTitleData = async (titleToSearch: string) => {
		if (!selectedEntityId1 || !selectedEntityId2 || !titleToSearch) return;
    setLoading(true);

    try {
      setLoading(true);
      const payload = { 
        entity1_id: Number(selectedEntityId1), 
        entity2_id: Number(selectedEntityId2),
				title: titleToSearch,
        name: titleToSearch,
      }
      const selectedSharedJobTitleEncodedParams = encodeURIComponent(JSON.stringify(payload));
      const selectedSharedJobTitleSearchString = `?function=compareEntityData&parameter=${selectedSharedJobTitleEncodedParams}`;

			const res = await fetch(buildApiUrl(apiEndpoint, selectedSharedJobTitleSearchString));
      const sharedJobTitleData: SharedJobData[] = await res.json();

			searchOptionType === "Custom Search" && sharedJobTitleData.length < 1 ? setNoCustomSearchResults(true) : setNoCustomSearchResults(false);

			setMasterData(sharedJobTitleData);
			setFilteredData(sharedJobTitleData);

			// Filter data for each entity
			const entity1OnlySharedJobTitleData = sharedJobTitleData.filter((entity) => entity.entity_name === selectedEntityName1);
			const entity2OnlySharedJobTitleData = sharedJobTitleData.filter((entity) => entity.entity_name === selectedEntityName2);

			setEntity1Data(entity1OnlySharedJobTitleData);
			setEntity2Data(entity2OnlySharedJobTitleData);

      const sortedSharedJobTitles = [...new Set((sharedJobTitleData as any[]).map((item: any) => item.title))]
        .sort((a: any, b: any) => a.localeCompare(b)) as string[];
      setAllEmployeeTitles(sortedSharedJobTitles);

      const sortedSharedEmployeeNames = [...new Set((sharedJobTitleData as any[]).map((item: any) => item.employee_name))]
        .sort((a: any, b: any) => a.localeCompare(b)) as string[];
      setAllEmployeeNames(sortedSharedEmployeeNames);

      const sortedSharedFiscalYears = [...new Set(sharedJobTitleData.map((item: any) => Number(item.fiscal_year)))]
  			.sort((a: any, b: any) => b - a) as number[];
      setAllFiscalYears(sortedSharedFiscalYears);
      
    } catch (error) {
      console.error("Error in the getCommonJobTitleData() method");
    }
  }

	const createTable = () => {
		const controller = new AbortController();
    const signal = controller.signal;

		// Added in case data hasn't been filtered yet
		const initialData = (filteredData && filteredData.length > 0) ? filteredData : masterData;

		if (!signal.aborted && Array.isArray(initialData)) {
			const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) && window.innerWidth < 1025;

			if (!tableRef.current) return;
			
			tabulatorRef.current = new Tabulator(tableRef.current, {
				data: initialData,
				minHeight: "450px",
				maxHeight: "100%",
				layout: isMobile ? "fitDataFill" : "fitColumns",
				pagination: "local",
				paginationSize: 10,
				placeholder: "No Data Available",
				headerSort: true,
				initialSort: [
					{
						column: "total",
						dir: "desc",
					}
				],
				columns: [
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-briefcase'></i> Title";
							return el;
						},
						title: "Title",
						field: "title",
					},
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-user'></i> Employee";
							return el;
						},
						title: "Employee",
						field: "employee_name",
					},
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-city'></i> Gov. Type";
							return el;
						},
						title: "Government",
						field: "govt_lvl",
					},
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-landmark'></i> Entity";
							return el;
						},
						title: "Entity",
						field: "entity_name",
					},
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-calendar'></i> Year";
							return el;
						},
						title: "Fiscal Year",
						field: "fiscal_year",
					},
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-dollar-sign'></i> Wages";
							return el;
						},
						title: "Wages",
						field: "wages",
						sorter: "number",
						formatter: "money",
						headerSort: true,
						formatterParams: { symbol: "$" }
					},
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-dollar-sign'></i> Benefits";
							return el;
						},
						title: "Benefits",
						field: "benefits",
						formatter: "money",
						formatterParams: { symbol: "$" }
					},
					{
						titleFormatter: () => {
							const el = document.createElement("span");
							el.innerHTML = "<i class='fas fa-dollar-sign'></i> Total";
							return el;
						},
						title: "Total",
						field: "total",
						sorter: "number",
						formatter: "money",
						formatterParams: { symbol: "$" }
					},
				],
			});
		}
	}

	const createPercentilesAndSummaries = (data: SharedJobData[], key: string) => {
		try {
			// Implement Chart Type into summary data and create filters and percentiles
			const getPercentile = (array: number[], percentile: number): number => {
				if (array.length === 0) return 0;
				if (array.length === 1) return array[0];
				const index = (array.length - 1) * percentile;
				const lower = Math.floor(index);
				const upper = Math.ceil(index);
				const weight = index - lower;
				return Math.round(array[lower] * (1 - weight) + array[upper] * weight);
			}

			switch (chartType) {
				case "Total Compensation":
					// Summary Data
					const totalCompDataLength: number = (key === "Entity 1" ? entity1Data?.length : entity2Data?.length) ?? 0;
					const totalCompWages = (data || []).reduce(
					  (prev: number, current: any) => {
					    return Math.round(prev + current.total);
					  }, 
					  0
					);
					const totalCompMean = totalCompDataLength > 0 
						? Math.round(totalCompWages / totalCompDataLength) : 0;
					const totalCompVariance = totalCompDataLength > 0 
					  ? (data || []).reduce((prev: number, current: any) => {
					      const difference = current.total - totalCompMean;
					      const squaredDifference = Math.pow(difference, 2);
					      return prev + squaredDifference;
					    }, 0) / totalCompDataLength
					  : 0;
	        const totalCompStandardDeviation = Math.round(Math.sqrt(totalCompVariance));

					if (key === "Entity 1") {
						setEntity1SummaryData({
		          observations: totalCompDataLength,
		          mean: totalCompMean,
		          standard_deviation: totalCompStandardDeviation,
		          total_sum: totalCompWages,
		        })
					} else if (key === "Entity 2") {
						setEntity2SummaryData({
		          observations: totalCompDataLength,
		          mean: totalCompMean,
		          standard_deviation: totalCompStandardDeviation,
		          total_sum: totalCompWages,
		        })
					}

	        // Percentile Data
					const totalCompSortedWages = (data ||[])
						.map((item: any) => Math.round(item.total))
						.sort((a: any, b: any) => a - b);
	        const totalCompLowestWage = totalCompSortedWages[0];
					const totalCompTwentyFifthPercentile = getPercentile(totalCompSortedWages, 0.25);
					const totalCompSeventyFifthPercentile = getPercentile(totalCompSortedWages, 0.75);
					const totalCompMedian = getPercentile(totalCompSortedWages, 0.50)
					const totalCompMaximum = totalCompSortedWages[totalCompSortedWages.length - 1] || 0;

					if (key === "Entity 1") {
						setEntity1PercentileData({
		          minimum: totalCompLowestWage,
		          twenty_fifth_percentile: totalCompTwentyFifthPercentile,
		          median: totalCompMedian,
		          seventy_fifth_percentile: totalCompSeventyFifthPercentile,
		          maximum: totalCompMaximum,
	        	}) 
					} else if (key === "Entity 2") {
						setEntity2PercentileData({
		          minimum: totalCompLowestWage,
		          twenty_fifth_percentile: totalCompTwentyFifthPercentile,
		          median: totalCompMedian,
		          seventy_fifth_percentile: totalCompSeventyFifthPercentile,
		          maximum: totalCompMaximum,
	        	})
					}
	        break;
				
				case "Wages Only":
	        // Summary Data
					const wagesOnlyDataLength: number = (key === "Entity 1" ? entity1Data?.length : entity2Data?.length) ?? 0;
					const wagesOnlyTotalWages = (data || []).reduce(
					  (prev: number, current: any) => {
					    return Math.round(prev + current.wages);
					  }, 
					  0
					);
					const wagesOnlyMean = wagesOnlyDataLength > 0 ? Math.round(wagesOnlyTotalWages / wagesOnlyDataLength) : 0;
					const wagesOnlyVariance = wagesOnlyDataLength > 0 ? (data || []).reduce((prev: number, current: any) => {
						const wagesOnlyDifference = current.wages - wagesOnlyMean;
						const wagesOnlysquaredDifference = Math.pow(wagesOnlyDifference, 2);
						return prev + wagesOnlysquaredDifference;
					}, 0) / wagesOnlyDataLength : 0;
	        const wagesOnlyStandardDeviation = Math.round(Math.sqrt(wagesOnlyVariance));

					if (key === "Entity 1") {
						setEntity1SummaryData({
		          observations: wagesOnlyDataLength,
		          mean: wagesOnlyMean,
		          standard_deviation: wagesOnlyStandardDeviation,
		          total_sum: wagesOnlyTotalWages,
		        });
					} else if (key === "Entity 2") {
						setEntity2SummaryData({
		          observations: wagesOnlyDataLength,
		          mean: wagesOnlyMean,
		          standard_deviation: wagesOnlyStandardDeviation,
		          total_sum: wagesOnlyTotalWages,
		        });
					}

	        // Percentile Data
					const wagesOnlySortedWages = (data ||[])
						.map((item: any) => Math.round(item.wages))
						.sort((a: any, b: any) => a - b);
	        const wagesOnlyLowestWage = wagesOnlySortedWages[0] || 0;
					const wagesOnlyTwentyFifthPercentile = getPercentile(wagesOnlySortedWages, 0.25);
					const wagesOnlyMedian = getPercentile(wagesOnlySortedWages, 0.50);
					const wagesOnlySeventyFifthPercentile = getPercentile(wagesOnlySortedWages, 0.75);
	        const wagesOnlyMaximum = wagesOnlySortedWages[wagesOnlySortedWages.length - 1] || 0;

					if (key === "Entity 1") {
						setEntity1PercentileData({
		          minimum: wagesOnlyLowestWage,
		          twenty_fifth_percentile: wagesOnlyTwentyFifthPercentile,
		          median: wagesOnlyMedian,
		          seventy_fifth_percentile: wagesOnlySeventyFifthPercentile,
		          maximum: wagesOnlyMaximum,
		        })
					} else if (key === "Entity 2") {
						setEntity2PercentileData({
		          minimum: wagesOnlyLowestWage,
		          twenty_fifth_percentile: wagesOnlyTwentyFifthPercentile,
		          median: wagesOnlyMedian,
		          seventy_fifth_percentile: wagesOnlySeventyFifthPercentile,
		          maximum: wagesOnlyMaximum,
		        })
					}
	        break;

				case "Benefits Only":
					const benefitsOnlyDataLength: number = (key === "Entity 1" ? entity1Data?.length : entity2Data?.length) ?? 0;
					const benefitsOnlyTotalWages = (data).reduce(
					  (prev: number, current: any) => {
					    return Math.round(prev + current.benefits);
					  }, 
					  0
					);
					const benefitsOnlyMean = benefitsOnlyDataLength > 0 ? Math.round(benefitsOnlyTotalWages / benefitsOnlyDataLength) : 0;
					const benefitsOnlyVariance = benefitsOnlyDataLength > 0 ? (data || []).reduce((prev: number, current: any) => {
						const difference = current.benefits - benefitsOnlyMean;
						const benefitsOnlySquaredDifference = Math.pow(difference, 2);
						return prev + benefitsOnlySquaredDifference;
					}, 0) / benefitsOnlyDataLength : 0;
	        const benefitsOnlyStandardDeviation = Math.round(Math.sqrt(benefitsOnlyVariance));

					if (key === "Entity 1") {
						setEntity1SummaryData({
		          observations: benefitsOnlyDataLength,
		          mean: benefitsOnlyMean,
		          standard_deviation: benefitsOnlyStandardDeviation,
		          total_sum: benefitsOnlyTotalWages,
		        });
					} else if (key === "Entity 2") {
						setEntity2SummaryData({
		          observations: benefitsOnlyDataLength,
		          mean: benefitsOnlyMean,
		          standard_deviation: benefitsOnlyStandardDeviation,
		          total_sum: benefitsOnlyTotalWages,
		        });
					}

	        // Percentile Data
					const benefitsOnlySortedWages = (data ||[])
						.map((item: any) => Math.round(item.benefits))
						.sort((a: any, b: any) => a - b);
	        const benefitsOnlyLowestWage = benefitsOnlySortedWages[0] || 0;
					const benefitsOnlyTwentyFifthPercentile = getPercentile(benefitsOnlySortedWages, 0.25);
					const benefitsOnlyMedian = getPercentile(benefitsOnlySortedWages, 0.50);
					const benefitsOnlySeventyFifthPercentile = getPercentile(benefitsOnlySortedWages, 0.75);
	        const benefitsOnlyMaximum = benefitsOnlySortedWages[benefitsOnlySortedWages.length - 1] || 0;

					if (key === "Entity 1") {
						setEntity1PercentileData({
		          minimum: benefitsOnlyLowestWage,
		          twenty_fifth_percentile: benefitsOnlyTwentyFifthPercentile,
		          median: benefitsOnlyMedian,
		          seventy_fifth_percentile: benefitsOnlySeventyFifthPercentile,
		          maximum: benefitsOnlyMaximum,
		        })
					} else if (key === "Entity 2") {
						setEntity2PercentileData({
		          minimum: benefitsOnlyLowestWage,
		          twenty_fifth_percentile: benefitsOnlyTwentyFifthPercentile,
		          median: benefitsOnlyMedian,
		          seventy_fifth_percentile: benefitsOnlySeventyFifthPercentile,
		          maximum: benefitsOnlyMaximum,
		        })
					}
	        break;
	      default:
			}

			// Sorted variables for filter creation
	    const sortedEmployeeTitles = [...new Set(masterData?.map((item: any) => item.title))]
	      .sort((a: any, b: any) => a.localeCompare(b)) as string[];
	    setAllEmployeeTitles(sortedEmployeeTitles);

	    const sortedEmployeeNames = [...new Set(masterData?.map((item: any) => item.employee_name))]
	      .sort((a: any, b: any) => a.localeCompare(b)) as string[];
	    setAllEmployeeNames(sortedEmployeeNames);

			const sortedFiscalYears = [...new Set(masterData?.map((item: any) => Number(item.fiscal_year)))]
	      .sort((a: any, b: any) => b - a) as number[];
	    setAllFiscalYears(sortedFiscalYears);

		} catch (err: any) {
			console.error("Error in createPercentilesAndSummaries");
		} finally {
			setLoading(false);
		}
	};

	const createFilters = (dataToFilter: SharedJobData[], key: string, currentTitles: string[], currentNames: string[], currentFiscalYears: number[], selectedEntityName: string) => {
  	if (!dataToFilter || dataToFilter.length === 0) return;

		// Added to filter data by specific entity and prevent calucalations errors
		const entityFilteredData = dataToFilter.filter((item: any) => item.entity_name === selectedEntityName);

		let data = [...entityFilteredData];

		// Employee Title Filter
		if (currentTitles && currentTitles.length > 0) {
			data = data.filter(item => currentTitles.includes(item.title));
		}

		// Employee Name Filter
  	if (currentNames && currentNames.length > 0) {
    	data = data.filter(item => currentNames.includes(item.employee_name));
  	}

		// Updated Fiscal Year Filter
		if (currentFiscalYears && currentFiscalYears.length > 0) {
			data = data.filter(item => currentFiscalYears.includes(item.fiscal_year));
		}

		// Filter Maximum Range Salary
		if (maximumRangeSalary > 0) {
			data = data.filter(item => item.total <= maximumRangeSalary);
		}

		// Filter Minimum Range Salary
		if (minimumRangeSalary > 0) {
			data = data.filter(item => item.total >= minimumRangeSalary);
		}

  	const filteredDataLength = data.length;

		if (filteredDataLength > 0) {
			switch (chartType) {
				case "Total Compensation":
					// Filtered Total Compensation Summary Data
					const filteredTotalCompWages = data.reduce((prev: number, current: any) => {
						return Math.round(prev + current.total);
					}, 0);

					const filteredTotalCompMean = Math.round(filteredTotalCompWages / filteredDataLength);
					const filteredTotalCompVariance = data.reduce((prev: number, current: any) => {
						const filteredTotalCompDifference = current.total - filteredTotalCompMean;
						const filteredTotalCompSquaredDifference = Math.pow(filteredTotalCompDifference, 2);
						return prev + filteredTotalCompSquaredDifference;
					}, 0) / filteredDataLength;
					const filteredTotalCompStandardDeviation = Math.round(Math.sqrt(filteredTotalCompVariance));

					if (key === "Entity 1") {
						setEntity1FilteredSummaryData({
							observations: filteredDataLength,
							mean: filteredTotalCompMean,
							standard_deviation: filteredTotalCompStandardDeviation,
							total_sum: filteredTotalCompWages,
						});
					} else if (key === "Entity 2") {
						setEntity2FilteredSummaryData({
							observations: filteredDataLength,
							mean: filteredTotalCompMean,
							standard_deviation: filteredTotalCompStandardDeviation,
							total_sum: filteredTotalCompWages,
						});
					}

					// Filtered Total Compensation Percentile Data
					const filteredTotalCompSortedWages = data.map((item: any) => Math.round(item.total)).sort((a: any , b: any) => a - b);
					const filteredTotalCompLowestWage = filteredTotalCompSortedWages[0];
					const filteredTotalCompTwentyFifthindex = Math.floor(filteredTotalCompSortedWages.length * 0.25);
					const filteredTotalCompSeventyFifthindex = Math.floor(filteredTotalCompSortedWages.length * 0.75);
					const filteredTotalCompTwentyFifthPercentile = filteredTotalCompSortedWages[filteredTotalCompTwentyFifthindex];
					const filteredTotalCompSeventyFifthPercentile = filteredTotalCompSortedWages[filteredTotalCompSeventyFifthindex];
					const filteredTotalCompMid = Math.floor(filteredDataLength / 2);
					const filteredTotalCompMedian = filteredDataLength % 2 !== 0
						? filteredTotalCompSortedWages[filteredTotalCompMid]
						: (filteredTotalCompSortedWages[filteredTotalCompMid - 1] + filteredTotalCompSortedWages[filteredTotalCompMid]) / 2;
					const filteredTotalCompMaximum = filteredTotalCompSortedWages[filteredTotalCompSortedWages.length - 1];

					if (key === "Entity 1") {
						setEntity1FilteredPercentileData({
							minimum: filteredTotalCompLowestWage,
							twenty_fifth_percentile: filteredTotalCompTwentyFifthPercentile,
							median: filteredTotalCompMedian,
							seventy_fifth_percentile: filteredTotalCompSeventyFifthPercentile,
							maximum: filteredTotalCompMaximum,
						});
					} else if (key === "Entity 2") {
						setEntity2FilteredPercentileData({
							minimum: filteredTotalCompLowestWage,
							twenty_fifth_percentile: filteredTotalCompTwentyFifthPercentile,
							median: filteredTotalCompMedian,
							seventy_fifth_percentile: filteredTotalCompSeventyFifthPercentile,
							maximum: filteredTotalCompMaximum,
						});
					}
					break;
			
				case "Wages Only":
					// Filtered Wages Only Summary Data
					const filteredWagesOnlyDataLength = data.length;

					// Filtered Wages Only Percentile Data
					const filteredWagesOnlySortedWages = data.map((item: any) => Math.round(item.wages)).sort((a: any , b: any) => a - b);
					const filteredWagesOnlyLowestWage = filteredWagesOnlySortedWages[0];
					const filteredWagesOnlyTwentyFifthindex = Math.floor(filteredWagesOnlySortedWages.length * 0.25);
					const filteredWagesOnlySeventyFifthindex = Math.floor(filteredWagesOnlySortedWages.length * 0.75);
					const filteredWagesOnlyTwentyFifthPercentile = filteredWagesOnlySortedWages[filteredWagesOnlyTwentyFifthindex];
					const filteredWagesOnlySeventyFifthPercentile = filteredWagesOnlySortedWages[filteredWagesOnlySeventyFifthindex];
					const filteredWagesOnlyMid = Math.floor(filteredWagesOnlyDataLength / 2);
					const filteredWagesOnlyMedian = filteredWagesOnlyDataLength % 2 !== 0
						? filteredWagesOnlySortedWages[filteredWagesOnlyMid]
						: (filteredWagesOnlySortedWages[filteredWagesOnlyMid - 1] + filteredWagesOnlySortedWages[filteredWagesOnlyMid]) / 2;
					const filteredWagesOnlyMaximum = filteredWagesOnlySortedWages[filteredWagesOnlySortedWages.length - 1];

					// Update for Range Filters
					if (key === "Entity 1") {
						setEntity1FilteredPercentileData({
							minimum: filteredWagesOnlyLowestWage,
							twenty_fifth_percentile: filteredWagesOnlyTwentyFifthPercentile,
							median: filteredWagesOnlyMedian,
							seventy_fifth_percentile: filteredWagesOnlySeventyFifthPercentile,
							maximum: filteredWagesOnlyMaximum,
						})
					} else if (key === "Entity 2") {
						setEntity2FilteredPercentileData({
							minimum: filteredWagesOnlyLowestWage,
							twenty_fifth_percentile: filteredWagesOnlyTwentyFifthPercentile,
							median: filteredWagesOnlyMedian,
							seventy_fifth_percentile: filteredWagesOnlySeventyFifthPercentile,
							maximum: filteredWagesOnlyMaximum,
						})
					}
					break;
			
				case "Benefits Only":
					// Summary Data
					const filteredBenefitsOnlyDataLength = data.length;

					// Percentile Data
					const filteredBenefitsOnlySortedWages = data.map((item: any) => Math.round(item.benefits)).sort((a: any , b: any) => a - b);
					const filteredBenefitsOnlyLowestWage = filteredBenefitsOnlySortedWages[0];
					const filteredBenefitsOnlyTwentyFifthindex = Math.floor(filteredBenefitsOnlySortedWages.length * 0.25);
					const filteredBeneftitsOnlySeventyFifthindex = Math.floor(filteredBenefitsOnlySortedWages.length * 0.75);
					const filteredBenefitsOnlyTwentyFifthPercentile = filteredBenefitsOnlySortedWages[filteredBenefitsOnlyTwentyFifthindex];
					const filteredBenefitsOnlySeventyFifthPercentile = filteredBenefitsOnlySortedWages[filteredBeneftitsOnlySeventyFifthindex];
					const filteredBenefitsOnlyMid = Math.floor(filteredBenefitsOnlyDataLength / 2);
					const filteredBenefitsOnlyMedian = filteredBenefitsOnlyDataLength % 2 !== 0
						? filteredBenefitsOnlySortedWages[filteredBenefitsOnlyMid]
						: (filteredBenefitsOnlySortedWages[filteredBenefitsOnlyMid - 1] + filteredBenefitsOnlySortedWages[filteredBenefitsOnlyMid]) / 2;
					const filteredBenefitsOnlyMaximum = filteredBenefitsOnlySortedWages[filteredBenefitsOnlySortedWages.length - 1];

					if (key === "Entity 1") {
						setEntity1FilteredPercentileData({
							minimum: filteredBenefitsOnlyLowestWage,
							twenty_fifth_percentile: filteredBenefitsOnlyTwentyFifthPercentile,
							median: filteredBenefitsOnlyMedian,
							seventy_fifth_percentile: filteredBenefitsOnlySeventyFifthPercentile,
							maximum: filteredBenefitsOnlyMaximum,
						});
					} else if (key === "Entity 2") {
						setEntity2FilteredPercentileData({
							minimum: filteredBenefitsOnlyLowestWage,
							twenty_fifth_percentile: filteredBenefitsOnlyTwentyFifthPercentile,
							median: filteredBenefitsOnlyMedian,
							seventy_fifth_percentile: filteredBenefitsOnlySeventyFifthPercentile,
							maximum: filteredBenefitsOnlyMaximum,
						});
					}
					break;
				}

		} else {
				setEntity1FilteredSummaryData({ observations: 0, mean: 0, standard_deviation: 0, total_sum: 0 });
				setEntity2FilteredSummaryData({ observations: 0, mean: 0, standard_deviation: 0, total_sum: 0 });
				setEntity1FilteredPercentileData({ minimum: 0, twenty_fifth_percentile: 0, median: 0, seventy_fifth_percentile: 0, maximum: 0 });
				setEntity2FilteredPercentileData({ minimum: 0, twenty_fifth_percentile: 0, median: 0, seventy_fifth_percentile: 0, maximum: 0 });
			}

   	setFilteredData(data);
 	};

	const handleMinSalaryChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = Math.min(Number(event.target.value), maximumRangeSalary - 1000);
		setMinimumRangeSalary(value);
	}

	const handleMaxSalaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Math.max(Number(e.target.value), minimumRangeSalary + 1000);
		setMaximumRangeSalary(value);
	};

  // Get All Entities on initial page load
  useEffect(() => {
    getAllEntities();
  }, []);

  // Get Common Titles
  useEffect(() => {
    if (!selectedEntityName1 || !selectedEntityName2 || !allEntities) return;

    const getEntityId = (data: Entity[] | null, selectedEntityName: string) => {
      return data?.find(item => item.entity_name === selectedEntityName)?.entity_id.toString() ?? null;
    };

    const id1 = getEntityId(allEntities, selectedEntityName1);
    const id2 = getEntityId(allEntities, selectedEntityName2);

    setSelectedEntityId1(id1);
    setSelectedEntityId2(id2);

    const getSharedJobTitles = async (id1: string, id2: string) => {
      try {
        setLoading(true);
        const payload = { entity1_id: id1, entity2_id: id2 };
        const encodedParams = encodeURIComponent(JSON.stringify(payload));
				const searchString = searchOptionType === "Shared Titles" || searchOptionType === "Custom Search" ? `?function=getJobTitleAvailableShared&parameter=${encodedParams}` : `?function=getJobTitleNgramAvailableShared&parameter=${encodedParams}`
        
				const res = await fetch(buildApiUrl(apiEndpoint, searchString));
        const commonTitleData = res.ok ? await res.json() : [];

				if (searchOptionType === "Custom Search") {
			    const matchExists = commonTitleData.some((item: any) => 
			        item.title.toLowerCase() === customSearchText?.toLowerCase()
			    );
			    setNoCustomSearchResults(!matchExists);
				}

				commonTitleData.length > 0 ? setNoCommonTitles(false) : setNoCommonTitles(true);

        setAllSharedJobTitles(commonTitleData);
				setAllSharedPartialJobTitles(commonTitleData);
      } catch (error) {
        console.error("Error in the getSharedJobTitles function");
      } finally {
        setLoading(false);
      }
    };

    if (id1 && id2) {
      getSharedJobTitles(id1, id2);
    }

  }, [selectedEntityName1, selectedEntityName2, allEntities, chartType]); 

	// Instantiate Tabulator once masterData is fetched
	useEffect(() => {
		if (masterData && masterData.length > 0) {
			createTable();
		} 

		// Clean up table instance if masterData resets or component unmounts
		return () => {
			if (tabulatorRef.current) {
				tabulatorRef.current.destroy();
				tabulatorRef.current = null;
			}
		};
	}, [masterData]);

	// Safely push filtered changes to Tabulator only once per render
	useEffect(() => {
		if (!tabulatorRef.current || !masterData || loading) return;

		let globalFilteredRows = [...masterData];
		
		if (selectedEmployeeTitles && selectedEmployeeTitles.length > 0) {
			globalFilteredRows = globalFilteredRows.filter(item => selectedEmployeeTitles.includes(item.title));
		}
		if (selectedEmployeeNames && selectedEmployeeNames.length > 0) {
			globalFilteredRows = globalFilteredRows.filter(item => selectedEmployeeNames.includes(item.employee_name));
		}
		if (selectedFiscalYears && selectedFiscalYears.length > 0) {
			globalFilteredRows = globalFilteredRows.filter(item => selectedFiscalYears.includes(item.fiscal_year));
		}
		if (maximumRangeSalary > 0) {
			globalFilteredRows = globalFilteredRows.filter(item => item.total <= maximumRangeSalary);
		}
		if (minimumRangeSalary > 0) {
			globalFilteredRows = globalFilteredRows.filter(item => item.total >= minimumRangeSalary);
		}

		// Force Tabulator to draw the combined multi-entity dataset cleanly
		const timer = setTimeout(() => {
			// Check that another click didn't kill the instance during the timeout
			if (tabulatorRef.current && tabulatorRef.current.element) {
				tabulatorRef.current.setData(globalFilteredRows);
			}
		}, 50);

		return () => clearTimeout(timer);
	}, [selectedEmployeeTitles, selectedEmployeeNames, selectedFiscalYears, minimumRangeSalary, maximumRangeSalary, masterData]);

	// Update percentiles and summary data when a new common title is selected
	useEffect(() => {
	  if (entity1Data) createPercentilesAndSummaries(entity1Data, "Entity 1");
	  if (entity2Data) createPercentilesAndSummaries(entity2Data, "Entity 2");
	}, [entity1Data, entity2Data, chartType, selectedEmployeeNames, selectedEmployeeTitles, selectedFiscalYears]);

	// Update filters when selected
	useEffect(() => {
	  if (entity1Data) {
	    createFilters(entity1Data, "Entity 1", selectedEmployeeTitles || [], selectedEmployeeNames || [], selectedFiscalYears || [], selectedEntityName1 || ""); // Updated to include selectedEntityName1 so filters only apply to one entity
	  }
	  if (entity2Data) {
	    createFilters(entity2Data, "Entity 2", selectedEmployeeTitles || [], selectedEmployeeNames || [], selectedFiscalYears || [], selectedEntityName2 || ""); // Updated to include selectedEntityName2 so filters only apply to one entity
	  }
	  setLoading(false);
	}, [selectedEmployeeTitles, selectedEmployeeNames, selectedFiscalYears, minimumRangeSalary, maximumRangeSalary, chartType]);

	// Original Update Table when masterData updates, scan masterData to establish min and max for slider boundaries
	useEffect(() => {
	  if (masterData && masterData.length > 0) {
	    const totals = masterData
	      .map(item => item.total)
	      .filter((val): val is number => typeof val === 'number');

	    if (totals.length > 0) {
	      const minValue = Math.min(...totals);
	      const maxValue = Math.max(...totals);

	      setMinimumRangeSalary(minValue);
	      setMaximumRangeSalary(maxValue);
	      setRangeFilterFloor(minValue);
	      setRangeFilterCeiling(maxValue);
	    }
	  }
	}, [masterData]);
	
	// Reset filters on new vendor search
	useEffect(() => {		
		if (!Array.isArray(masterData) || masterData.length === 0) return;

		setSelectedEmployeeTitles(null);
		setSelectedEmployeeNames(null);
		setSelectedFiscalYears(null);

		if (tabulatorRef.current) {
      setTimeout(() => {
				const tableInstance = (tabulatorRef.current as any).table || tabulatorRef.current;

        if (tableInstance && typeof tableInstance.setData === "function") {
            tableInstance.setData(masterData).then(() => {
                setTimeout(() => {
                    tableInstance.redraw(true); 
                }, 100);
            });
        }
      }, 100);
    }
	}, [masterData]);

	// Update min and max filters after a filter is selected
	useEffect(() => {
		if (!masterData || masterData.length === 0) return;

		const field = chartType === "Wages Only" ? "wages" : chartType === "Benefits Only" ? "benefits" : "total";
  
		// Create filtered subset of masterData
		let filteredSubset = [...masterData];
		if (selectedEmployeeTitles && selectedEmployeeTitles.length > 0) {
			filteredSubset = filteredSubset.filter(item => selectedEmployeeTitles.includes(item.title));
		}
		if (selectedEmployeeNames && selectedEmployeeNames.length > 0) {
			filteredSubset = filteredSubset.filter(item => selectedEmployeeNames.includes(item.employee_name));
		}
		if (selectedFiscalYears && selectedFiscalYears.length > 0) {
			filteredSubset = filteredSubset.filter(item => selectedFiscalYears.includes(item.fiscal_year));
		}

  	const values = filteredSubset.map((item: any) => Number(item[field])).filter(val => !isNaN(val));

		if (values.length > 0) {
			const currentMin = Math.min(...values);
			const currentMax = Math.max(...values);

			// Update min and max range salaries only if they change
			if (minimumRangeSalary !== currentMin) setMinimumRangeSalary(currentMin);
			if (maximumRangeSalary !== currentMax) setMaximumRangeSalary(currentMax);
		}
	}, [selectedEmployeeTitles, selectedEmployeeNames, selectedFiscalYears, chartType, masterData])

	// Render D3
	useEffect(() => {
		if (loading || !masterData?.length || !chartRef.current) return;

		const svg = d3.select(chartRef.current);
		svg.selectAll("*").remove();

		// Create Data Key
		const chartTypeSelection =
      chartType === "Total Compensation" ? "total" :
      chartType === "Wages Only" ? "wages" :
      chartType === "Benefits Only" ? "benefits" : "total";

		// Filter based on range filter salary updates
		const filteredMasterData = masterData.filter(d => {
      const value = +d[chartTypeSelection];
			const inSalaryRange = value >= minimumRangeSalary && 
        (maximumRangeSalary > 0 ? value <= maximumRangeSalary : true);
      const matchesName = (selectedEmployeeNames?.length ?? 0) > 0 
        ? selectedEmployeeNames!.includes(d.employee_name) 
        : true;
      const matchesTitle = (selectedEmployeeTitles?.length ?? 0) > 0 
        ? selectedEmployeeTitles!.includes(d.title) 
        : true;
			const matchesYear = (selectedFiscalYears?.length ?? 0) > 0 
        ? selectedFiscalYears!.map(String).includes(String(d.fiscal_year)) 
        : true;

      return inSalaryRange && matchesTitle && matchesName && matchesYear;
    });

		const margin = { top: 10, right: 10, bottom: 60, left: 80 };
    const width = containerRef.current?.clientWidth ?? 1200;
    const height = 500;

		// Updated Define the X Domain name with fallbacks
		const xDomain: [number, number] = filteredMasterData.length > 0 
		  ? [
        (minimumRangeSalary > 0 ? minimumRangeSalary : (d3.min(filteredMasterData, d => +d[chartTypeSelection]) || 0)),
        (maximumRangeSalary > 0 ? maximumRangeSalary : (d3.max(filteredMasterData, d => +d[chartTypeSelection]) || 100000))
      	]
		  : [0, 100000];

		// Define the X Scale Type
		let x: d3.ScaleContinuousNumeric<number, number> = d3.scaleLinear()
			.domain(xDomain)
			.range([margin.left, width - margin.right])
			.nice();

		// Updated Histogram Bins
		const groupedData = d3.groups(filteredMasterData, d => d.entity_name);

		// Create separate bins for each group
		const binnedSeries = groupedData.map(([name, data]) => {
		  const binned = d3.bin<SharedJobData, number>()
		    .value(d => +d[chartTypeSelection])
		    .domain(x.domain() as [number, number])
				.thresholds((numberOfBins || 20) as any)(data);
		  return { name, binned };
		});

		// Get global max for Y in all entities
		const yMax = d3.max(binnedSeries.flatMap(s => s.binned), d => d.length) || 0;

		// Create Y Scale
	  const y = d3.scaleLinear()
	    .domain([0, yMax === 0 && filteredMasterData.length > 0 ? 1 : yMax])
	    .nice()
	    .range([height - margin.bottom, margin.top]);

		// Add dimensions to SVG Container and Draw Chart
		svg.attr("width", width)
			.attr("height", height)
			.attr("viewBox", `0 0 ${width} ${height}`)
			.attr("style", "max-width: 100%; height: auto;");

		// Create Tooltips
		const tooltip = d3.select("body").append("div").attr("class", "tooltip").style("opacity", 0)
			.style("position", "absolute").style("pointer-events", "none").style("background-color", "#fff")
			.style("border", "1px solid #ccc").style("padding", "12px").style("border-radius", "6px").style("z-index", "1000");

		const colorScale = d3.scaleOrdinal<string>()
		  .domain([
		    selectedEntityName1 || "Entity 1", 
		    selectedEntityName2 || "Entity 2"
		  ])
		  .range(["#2e8b8b", "#ffc63a"]);

		// Create Series Groups
		const seriesG = svg.selectAll(".series")
		  .data(binnedSeries)
		  .join("g")
		  .attr("class", "series")
		  .attr("fill", d => colorScale(d.name))
		  .style("mix-blend-mode", "multiply")
		  .style("opacity", 0.8);

		// Draw the Rectangles
		seriesG.selectAll("rect")
		  .data(d => d.binned)
		  .join("rect")
		  .attr("x", (d) => x(d.x0 ?? 0) + 1)
		  .attr("width", (d) => Math.max(0, x(d.x1 ?? 0) - x(d.x0 ?? 0) - 1))
		  .attr("y", (d) => y(d.length))
	  .attr("height", (d) => y(0) - y(d.length))
	  .on("mouseover", (event, d) => {
	    tooltip.style("opacity", 1);
	    const parentData = d3.select(event.currentTarget.parentNode).datum() as any;
	    d3.select(event.currentTarget)
	      .attr("fill", d3.color(colorScale(parentData.name))?.darker(0.5)?.toString() || "#ccc");
	  })
	  .on("mousemove", (event, d: any) => {
	    const parentData = d3.select(event.currentTarget.parentNode).datum() as any;
	    
	    tooltip
	      .html(`
	        <div style="font-weight: bold; border-bottom: 1px solid #ccc; margin-bottom: 5px; padding-bottom: 2px;">
	          ${parentData.name}
	        </div>
	        <div style="font-size: 14px;">
	          Range: $${(d.x0 ?? 0).toLocaleString()} - $${(d.x1 ?? 0).toLocaleString()}
	        </div>
	        <div style="margin-top: 5px; color: #666;">
	          <strong>${d.length}</strong> ${d.length === 1 ? 'employee' : 'employees'}
	        </div>
	      `)
	      .style("left", (event.pageX + 15) + "px")
	      .style("top", (event.pageY - 28) + "px");
	  })
	  .on("mouseout", (event) => {
	    tooltip.style("opacity", 0);
	    const parentData = d3.select(event.currentTarget.parentNode).datum() as any;
	    d3.select(event.currentTarget).attr("fill", colorScale(parentData.name));
	  });

		const xTickFormat = d3.format("$.2s");

		// Add X axis and label
		svg.append("g")
			.attr("transform", `translate(0,${height - margin.bottom})`)
			.call(d3.axisBottom(x).ticks(width / 80).tickSizeOuter(0).tickFormat(xTickFormat as any))
			.call((g) => g.append("text")
				.attr("x", (width - margin.left - margin.right) / 2 + margin.left)
				.attr("y", margin.bottom - 10)
				.attr("fill", "currentColor")
				.attr("text-anchor", "middle")
				.attr("font-weight", "bold")
				.text(`${chartType} ($)`));

		// Add Y axis and label
		svg.append("g")
			.attr("transform", `translate(${margin.left},0)`)
			.call(d3.axisLeft(y).ticks(height / 40))
			.call((g) => g.select(".domain").remove())
			.call((g) => g.append("text")
				.attr("transform", "rotate(-90)")
				.attr("x", -(height - margin.top - margin.bottom) / 2 - margin.top)
				.attr("y", -margin.left + 15)
				.attr("fill", "currentColor")
				.attr("text-anchor", "middle")
				.attr("font-weight", "bold")
				.text("Number of Employees"));

		// Return the SVG element
    return () => { 
      tooltip.remove(); 
    };
	}, [masterData, loading, selectedEmployeeNames, selectedEmployeeTitles, selectedFiscalYears, chartType, minimumRangeSalary, maximumRangeSalary, numberOfBins]);

	// Updated memoized versions of options for multiselectasync dropdown forms
	const employeeTitleOptions = useMemo(() => 
		(allEmployeeTitles || []).map(title => ({ value: title, label: title})),
	[allEmployeeTitles]);

	const employeeNameOptions = useMemo(() => 
		(allEmployeeNames || []).map(name => ({ value: name, label: name })), 
	[allEmployeeNames]);

	const fiscalYearOptions = useMemo(() => 
		(allFiscalYears || []).map(year => ({ value: year.toString(), label: year.toString() })), 
	[allFiscalYears]);

  const titleTypeOptions = ["Shared Titles", "Shared Partial Titles", "Custom Search"];

	// Add options for chart type radio form
  const chartTypeOptions = ["Total Compensation", "Wages Only", "Benefits Only"];

  return (
    <>
      <HeaderComponent/>
      <FeatureNav/>
      <BreadCrumbs/>
      <Container fluid as="main">
        <Row className="mb-4 px-4">
          <Col xs={12} className="px-0">
            <Card bg="secondary" text="white" style={{ border: "none", borderRadius: "0" }}>
              <Card.Body style={{ justifyItems: "center", textAlign: "center" }}>
              <div className="tu_tabHeader">
                <div className="tu-tabHeader__titleRow">
                  <h1 className="tu-tabHeader__title">
                  Compare Two Entities</h1>
                    <button
                    type="button"
                    className="tu-tabHeader__infoBtn"
                    onClick={() => setShowHeaderInfo(!showHeaderInfo)}
                    aria-label="Compare Two Entities info"
                  >
                    <i className="fas fa-info"/>
                    </button>
                  </div>
                </div>
                <Collapse in={showHeaderInfo}>
                  <div>
                  <p className="lead mt-2 text-center">
                    This page allows you to compare compensation between two entities. First select the two entities you want to compare. Next, decide the job title option you want.
                  </p>
                    <ul>
                      <li><b>Shared Titles</b> - will find any jobs with an exact same spelling</li>
                      <li><b>Shared Partial Titles</b> - will find and jobs with partial match spellings</li>
                      <li><b>Free Search</b> - will match any jobs shared by both entities which match your search terms</li>
                    </ul>
                  <p className="lead mt-2 text-center">
                    Finally, click <b>Submit</b> to get your results.
                  </p>
                  </div>
                </Collapse>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Loader */}
        {loading ? <Loader /> : <></>}

        { /* Search Bar */}
				<Row className="px-4">
					<Form className="compare_two_entities_form" onSubmit={handleSubmit}>
						<Col xs={12} className="px-0">
							<Card>
								<Card.Header className="bg-secondary text-white" style={{ border: "none" }}>
									<h3 className="text-white">Search</h3>
								</Card.Header>
								<Card.Body>
									<Row>
										<Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
											<h5 className="d-flex flex-column">
												<span className="mb-1">
													<i className="fas fa-landmark"/> {" "}Select Entity 1
												</span>
											</h5>
											<SingleSelectAsync
												key={memoizedEntityNameOptions.length}
												value={selectedEntityName1 ? { label: selectedEntityName1, value: selectedEntityName1 } : null}
												onChange={(newValue) => setSelectedEntityName1(newValue ? newValue.value : null)}
												loadOptions={loadEntityOptions}
												defaultOptions={memoizedEntityNameOptions.length > 0 ? memoizedEntityNameOptions : true}
												type="entity"
											/>
										</Col>
										<Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
											<h5 className="d-flex flex-column">
												<span className="mb-1">
													<i className="fas fa-landmark"/> {" "}Select Entity 2
												</span>
											</h5>
											<SingleSelectAsync
												key={memoizedEntityNameOptions.length}
												value={selectedEntityName2 ? { label: selectedEntityName2, value: selectedEntityName2 } : null}
												onChange={(newValue) => setSelectedEntityName2(newValue ? newValue.value : null)}
												loadOptions={loadEntityOptions}
												defaultOptions={memoizedEntityNameOptions.length > 0 ? memoizedEntityNameOptions : true}
												type="entity"
											/>
										</Col>
									</Row>
									<Col xs={12} className="mt-2">
										<h5 className="d-flex flex-column">
											<span className="mb-2">
												<i className="fas fa-chart-area"/> {" "}Title Option
											</span>
											<div className="mb-2">
												{titleTypeOptions?.map((option: any) => (
													<Form.Check
														key={option}
														inline
														type="radio"
														label={option}
														name="titleOptionGroup"
														id={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
														checked={searchOptionType === option}
														onChange={() => setSearchOptionType(option as "Shared Titles" | "Shared Partial Titles" | "Custom Search")}
													/>
												))}
											</div>
										</h5>
									</Col>
									<Row>
										{(() => {
											if (searchOptionType === "Shared Titles") {
												return (
													<Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-2">
														<h5>
															<i className="fas fa-list-alt" /> Select Common Title
														</h5>
														<SingleSelectAsync
															key={memoizedSharedJobTitleOptions.length}
															value={selectedCommonTitle ? { label: selectedCommonTitle, value: selectedCommonTitle } : null}
															onChange={(newValue) => setSelectedCommonTitle(newValue ? newValue.value : null)}
															loadOptions={loadJobTitleSharedOptions}
															defaultOptions={memoizedSharedJobTitleOptions.length > 0 ? memoizedSharedJobTitleOptions : true}
															type="entity"
														/>
														{noCommonTitles && (
															<Form.Control.Feedback type="invalid" className="invalid-form">
																Error! There are no shared common titles between {selectedEntityName1? selectedEntityName1 : "Entity 1" } and {selectedEntityName2? selectedEntityName2 : "Entity 2" }. Please try a search with different entities.
															</Form.Control.Feedback>
														)}
													</Col>
												);
											} else if (searchOptionType === "Shared Partial Titles") {
												return (
													<Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
														<h5>
															<i className="fas fa-list-alt" /> Select Partial Title
														</h5>
														<SingleSelectAsync
															key={memoizedSharedJobTitleOptions.length}
															value={selectedCommonTitle ? { label: selectedCommonTitle, value: selectedCommonTitle } : null}
															onChange={(newValue) => setSelectedCommonTitle(newValue ? newValue.value : null)}
															loadOptions={loadJobTitleSharedPartialOptions}
															defaultOptions={memoizedSharedPartialJobTitleOptions.length > 0 ? memoizedSharedPartialJobTitleOptions : true}
															type="entity"
														/>
														{noCommonTitles && (
															<Form.Control.Feedback type="invalid" className="invalid-form">
																Error! There are no shared common partial titles between {selectedEntityName1? selectedEntityName1 : "Entity 1" } and {selectedEntityName2? selectedEntityName2 : "Entity 2" }. Please try a search with different entities.
															</Form.Control.Feedback>
														)}
													</Col>
												);
											} else {
												return (
													<Col xs={12} sm={12} md={4} lg={4} xl={4} className="my-3">
														<h5>
															<i className="fas fa-briefcase" /> Enter Job Title
														</h5>
														<Form.Control
															type="text"
															placeholder="Enter Job Title Here"
															value={customSearchText ?? ""}
															onChange={(event) => setCustomSearchText(event.target.value)}
															isInvalid={!!noCustomSearchResults}
															className="mt-2"
														/>
														{noCustomSearchResults && (
															<Form.Control.Feedback type="invalid" className="invalid-form">
																Error! There are no shared common results for that job at both {selectedEntityName1? selectedEntityName1 : "Entity 1" } and {selectedEntityName2? selectedEntityName2 : "Entity 2" }. Please try your custom search a different job title.
															</Form.Control.Feedback>
														)}
													</Col>
												);
											}
										})()}
									</Row>
									{/* Submit Button Section */}
									<Row>
										<Col xs={12} className="my-2">
											<button
												type="submit"
												className="btn btn-secondary"
												aria-label="Submit"
											>
												<i className="fas fa-search"></i>{" "}
												Search
											</button>
										</Col>
									</Row>
								</Card.Body>
							</Card>
						</Col>
					</Form>
				</Row>

				{/* Updated Filters */}
				<Row className="px-4">
				  <Col xs={12} className="h-100 px-0">
					  <Card className="mt-4">
							<Card.Header className="bg-secondary text-white" style={{ border: "none" }}>
                <h3 className="text-white">Filters</h3>
              </Card.Header>
							<Card.Body className="px-0 mx-0 pt-0">
                <Row className="px-4 mt-2">
									{/* Title Filter */}
									<Col xs={12} sm={12} md={6} lg={3} xl={3} className="mt-2">
									  <h5 className="d-flex flex-column">
											<span className="mb-2">
	                      <i className="fas fa-briefcase" />
	                      {" "}Filter Title
											</span>
                      <MultiSelectAsync
                        options={employeeTitleOptions}
                        defaultOptions={employeeTitleOptions}
                        value={selectedEmployeeTitles ? selectedEmployeeTitles.map(title => ({ value: title, label: title })) : []} 
                        onChange={(selected) => {
                          const titles = selected ? selected.map(s => s.value) : [];
                          setSelectedEmployeeTitles(titles);
													setFilterApplied(titles.length > 0); // Added to check if filters are applied or not and only show filtered summaries and percentiles if filters are selected
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return employeeTitleOptions;
                          return employeeTitleOptions.filter(o =>
                            o.label.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
									</Col>
									{/* End Title Filter */}

									{/* Employee Filter */}
									<Col xs={12} sm={12} md={6} lg={3} xl={3} className="mt-2">
									  <h5 className="d-flex flex-column">
											<span className="mb-2">
	                      <i className="fas fa-user"/>
	                      {" "}Filter Employee
											</span>
											<MultiSelectAsync
                        options={employeeNameOptions}
                        defaultOptions={employeeNameOptions}
                        value={selectedEmployeeNames ? selectedEmployeeNames.map(employee_name => ({ value: employee_name, label: employee_name })) : []} 
                        onChange={(selected) => {
                          const names = selected ? selected.map(s => s.value) : [];
                          setSelectedEmployeeNames(names);
													setFilterApplied(names.length > 0); // Added to check if filters are applied or not and only show filtered summaries and percentiles if filters are selected
                        }}
                        loadOptions={async (inputValue) => {
                          if (!inputValue) return employeeNameOptions;
                          return employeeNameOptions.filter(o =>
                            o.label.toLowerCase().includes(inputValue.toLowerCase())
                          );
                        }}
                        closeMenuOnSelect={false}
                      />
                    </h5>
									</Col>
									{/* End Employee Filter */}

									{/* Fiscal Year Filter */}
									<Col xs={12} sm={12} md={6} lg={3} xl={3} className="mt-2">
									  <h5 className="d-flex flex-column">
											<span className="mb-2">
	                      <i className="fas fa-calendar"/>
	                      {" "}Filter Year
											</span>
											<MultiSelectAsync
												options={fiscalYearOptions}
												defaultOptions={fiscalYearOptions}
												value={
													selectedFiscalYears?.map(year => ({
														value: year.toString(),
														label: year.toString(),
													})) || []
												}
												isSearchable={true}
												onChange={(selected) => {
													const values = selected ? selected.map(s => Number(s.value)) : [];
													setSelectedFiscalYears(values);
													setFilterApplied(values.length > 0); // Added to check if filters are applied or not and only show filtered summaries and percentiles if filters are selected
												}}
												loadOptions={async (inputValue) => {
													if (!inputValue) return fiscalYearOptions;
													return fiscalYearOptions.filter(o =>
														o.label.toString().toLowerCase().includes(inputValue.toLowerCase())
													);
												}}
												closeMenuOnSelect={false}
											/>
                  	</h5>
									</Col>
									{/* End Fiscal Year Filter */}

									{/* Range Filter */}
									<Col xs={12} sm={12} md={6} lg={3} xl={3} className="mt-2">
									  <h5>
                      <i className="fas fa-dollar-sign"/>
                      {" "}Filter Range
                    </h5>
                    <div className="filter-range-slider position-relative" style={{ height: "40px"}}>
                      {/* Min Slider */}
                      {rangeFilterCeiling > 0 ? (
                      <>
                        <label htmlFor="min-range-slider" className="sr-only" style={{ display: "none"}}>Minimum Salary</label>
                        <input 
                          type="range"
                          id="min-range-slider"
                          aria-label="minimum-salary-range"
                          aria-valuetext={`$${minimumRangeSalary}`}
                          min={rangeFilterFloor}
                          max={rangeFilterCeiling}
                          value={minimumRangeSalary}
                          onChange={handleMinSalaryChange}
                          className="form-range position-absolute w-100"
                          style={{ 
                            zIndex: (minimumRangeSalary > (rangeFilterCeiling / 2)) ? 10 : 5,
                            pointerEvents: "none",
                            background: "transparent"
                          }}
                        />
                      </>
                      ) : 
                      (<></>)
                      }
                      {/* Max Slider */}
                      { maximumRangeSalary > 0 ? (
                        <>
                          <label htmlFor="max-range-slider" className="sr-only" style={{ display: "none"}}>Maximum Salary</label>
                          <input 
                            type="range"
                            id="max-range-slider"
                            aria-label="maximum-salary-range"
                            aria-valuetext={`$${minimumRangeSalary}`} 
                            min={rangeFilterFloor}
                            max={rangeFilterCeiling}
                            value={maximumRangeSalary}
                            onChange={handleMaxSalaryChange}
                            className="form-range position-absolute w-100"
                            style={{ 
                              zIndex: 4,
                              pointerEvents: "none",
                              background: "transparent" 
                            }}
                          />
                        </>
                      ) : 
                      (<></>)
                      }
                    </div>
                    <div className="d-flex justify-content-between mt-2">
                      <span className="badge bg-light text-dark border">${minimumRangeSalary.toLocaleString()}</span>
                      <span className="badge bg-light text-dark border">${maximumRangeSalary.toLocaleString()}</span>
                    </div>
									</Col>

								</Row>

								<Row className="px-4 mt-2">
									{/* Chart Type Filter */}
									<Col xs={12} className="mt-2">
									  <h5>
											<i className="fas fa-chart-area"/>
											{" "}Filter Chart Type
											<Form>
												<div className="mb-2 mt-2">
													{chartTypeOptions.map((option) => (
														<Form.Check
															key={option}
															inline
															type="radio"
															label={option}
															name="chartTypeGroup"
															id={`radio-${option.replace(/\s+/g, '-').toLowerCase()}`}
															checked={chartType === option}
															onChange={() => setChartType(option as "Total Compensation" | "Wages Only" | "Benefits Only")}
														/>
													))}
												</div>
											</Form>
										</h5>
									</Col>
									{/* End Chart Type Filter */}

								</Row>
							</Card.Body>
						</Card>
					</Col>
				</Row>

				{/* Updated Entity Statistics Section */}
				<Row className="px-4">
					{/* Entity 1 */}
					<Col xs={12} sm={12} md={12} lg={6} xl={6} className="h-100 ps-md-0 statistics-section-mobile" >
					  <Card className="mt-4 shadow-sm">
							<Card.Header className="bg-secondary text-white">
								<h3 className="text-white">{selectedEntityName1 ? selectedEntityName1 : "Entity 1"}</h3>
							</Card.Header>
							<Card.Body style={{ paddingBottom: "44.5px", paddingTop: "44.5px" }}>

								{/* Entity 1 Summary Section */}
								<h4 className="mb-3">Summary</h4>
								<Row className="gx-2 gx-md mb-4">
									<StatisticsCard title="Observations" value={entity1SummaryData?.observations} isCurrency={false}/>
									<StatisticsCard title="Mean" value={entity1SummaryData?.mean}/>
									<StatisticsCard title="Standard Deviation" value={entity1SummaryData?.standard_deviation} />
									<StatisticsCard title="Total Sum" value={entity1SummaryData?.total_sum} />
								</Row>

								{/* Entity 1 Filtered Summary Section */}
								{filtersApplied && entity1FilteredSummaryData && (
									<>
										<h4 className="mb-3">Filtered Summary</h4>
										<Row className="gx-2 gx-md mb-4">
											<StatisticsCard title="Filtered Observations" value={entity1FilteredSummaryData?.observations} isCurrency={false} bg="primary-variation"/>
											<StatisticsCard title="Mean" value={entity1FilteredSummaryData?.mean} bg="primary-variation"/>
											<StatisticsCard title="Standard Deviation" value={entity1FilteredSummaryData?.standard_deviation} bg="primary-variation"/>
											<StatisticsCard title="Total Sum" value={entity1FilteredSummaryData?.total_sum} bg="primary-variation"/>
										</Row>
									</>
								)}

								{/* Entity 1 Percentile Section */}
								<h4 className="mb-3">Percentiles</h4>
								<Row className="gx-2 gx-md mb-4">
									<StatisticsCard title="Minimum" value={entity1PercentileData?.minimum} />
									<StatisticsCard title="25th Percentile" value={entity1PercentileData?.twenty_fifth_percentile} />
									<StatisticsCard title="Median"  value={entity1PercentileData?.median} />
									<StatisticsCard title="75th Percentile" value={entity1PercentileData?.seventy_fifth_percentile} />
									<StatisticsCard title="Maximum"  value={entity1PercentileData?.maximum}  />
								</Row>

								{/* Entity 1 Filtered Percentiles Section */}
								{filtersApplied && entity1FilteredPercentileData && (
									<>
										<h4 className="mb-3">Filtered Percentiles</h4>
										<Row className="gx-2 gx-md mb-4">
											<StatisticsCard title="Filtered Minimum" value={entity1FilteredPercentileData?.minimum} bg="primary-variation"/>
											<StatisticsCard title="25th Percentile" value={entity1FilteredPercentileData?.twenty_fifth_percentile} bg="primary-variation"/>
											<StatisticsCard title="median" value={entity1FilteredPercentileData?.median} bg="primary-variation"/>
											<StatisticsCard title="75h Percentile" value={entity1FilteredPercentileData?.seventy_fifth_percentile} bg="primary-variation"/>
											<StatisticsCard title="Maximum" value={entity1FilteredPercentileData?.maximum} bg="primary-variation"/>
										</Row>
									</>
								)}
							</Card.Body>
						</Card>
					</Col>

					{/* Entity 2 */}
					<Col xs={12} sm={12} md={12} lg={6} xl={6} className="h-100 pe-md-0 statistics-section-mobile">
					  <Card className="mt-4 shadow-sm">
							<Card.Header className="bg-primary text-black">
								<h3 className="text-black">{selectedEntityName2 ? selectedEntityName2 : "Entity 2"}</h3>
							</Card.Header>
							<Card.Body style={{ paddingBottom: "44.5px", paddingTop: "44.5px" }}>

								{/* Entity 2 Summary Section */}
								<h4 className="mb-3 text-black">Summary</h4>
								<Row className="gx-2 gx-md mb-4">
									<StatisticsCard title="Observations" value={entity2SummaryData?.observations} isCurrency={false} bg="primary"/>
									<StatisticsCard title="Mean" value={entity2SummaryData?.mean} bg="primary"/>
									<StatisticsCard title="Standard Deviation" value={entity2SummaryData?.standard_deviation} bg="primary"/>
									<StatisticsCard title="Total Sum" value={entity2SummaryData?.total_sum} bg="primary"/>
								</Row>

								{/* Entity 2 Filtered Summary Section */}
								{filtersApplied && entity2FilteredSummaryData && (
									<>
										<h4 className="mb-3">Filtered Summary</h4>
										<Row className="gx-2 gx-md mb-4">
											<StatisticsCard title="Filtered Observations" value={entity2FilteredSummaryData?.observations} isCurrency={false} bg="primary-variation"/>
											<StatisticsCard title="Mean" value={entity2FilteredSummaryData?.mean} bg="primary-variation"/>
											<StatisticsCard title="Standard Deviation" value={entity2FilteredSummaryData?.standard_deviation} bg="primary-variation"/>
											<StatisticsCard title="Total Sum" value={entity2FilteredSummaryData?.total_sum} bg="primary-variation"/>
										</Row>
									</>
								)}

								{/* Entity 2 Percentile Section */}
								<h4 className="mb-3">Percentiles</h4>
								<Row className="gx-2 gx-md mb-4">
									<StatisticsCard title="Minimum" value={entity2PercentileData?.minimum} bg="primary"/>
									<StatisticsCard title="25th Percentile" value={entity2PercentileData?.twenty_fifth_percentile} bg="primary"/>
									<StatisticsCard title="Median"  value={entity2PercentileData?.median} bg="primary"/>
									<StatisticsCard title="75th Percentile" value={entity2PercentileData?.seventy_fifth_percentile} bg="primary"/>
									<StatisticsCard title="Maximum"  value={entity2PercentileData?.maximum}  bg="primary"/>
								</Row>

								{/* Entity 2 Filtered Percentiles Section */}
								{filtersApplied && entity2FilteredPercentileData && (
									<>
										<h4 className="mb-3">Filtered Percentiles</h4>
										<Row className="gx-2 gx-md mb-4">
											<StatisticsCard title="Filtered Minimum" value={entity2FilteredPercentileData?.minimum} bg="primary-variation"/>
											<StatisticsCard title="25th Percentile" value={entity2FilteredPercentileData?.twenty_fifth_percentile} bg="primary-variation"/>
											<StatisticsCard title="median" value={entity2FilteredPercentileData?.median} bg="primary-variation"/>
											<StatisticsCard title="75h Percentile" value={entity2FilteredPercentileData?.seventy_fifth_percentile} bg="primary-variation"/>
											<StatisticsCard title="Maximum" value={entity2FilteredPercentileData?.maximum} bg="primary-variation"/>
										</Row>
									</>
								)}
							</Card.Body>
						</Card>
					</Col>
				</Row>

				{/* D3 Chart */}
				<Row className="pt-4 mx-0">
					<Col xs={12}>
						<Card bg="white">
							<Card.Header className="bg-secondary text-white">
								<h3 className="text-white">Cumulative Distribution (Histogram)</h3>
							</Card.Header>
							<Card.Body>
								<div id="chart-container" ref={containerRef} style={{ width: "100%" }}>
									{(!masterData || (masterData?.length ?? 0) <= 1) && (
										<p className="text-lead text-center py-5">
											Data must have at least 2 observations to plot a histogram. Please select a job title or refine your filters.
										</p>
									)}

									<div 
									className={(masterData?.length ?? 0) > 1 ? "d-block" : "d-none"}>
										<div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
											<div
												className="btn btn-secondary"
												id="btn-chart-download"
												onClick={() => {
													if (!chartRef.current) return;
													const serializer = new XMLSerializer();
													const svgString = serializer.serializeToString(chartRef.current);
													const canvas = document.createElement('canvas');
													const context = canvas.getContext('2d');
													const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
													const url = URL.createObjectURL(svgBlob);
													const img = new Image();
													img.onload = () => {
														canvas.width = img.width;
														canvas.height = img.height;
														if (context) {
															context.fillStyle = "white";
															context.fillRect(0, 0, canvas.width, canvas.height);
															context.drawImage(img, 0, 0);
														}
														URL.revokeObjectURL(url);
														const pngUrl = canvas.toDataURL('image/png');
														const a = document.createElement('a');
														a.download = `${formatStringForD3Download(selectedEntityName1 ?? "entity1")}_${formatStringForD3Download(selectedEntityName2 ?? "entity2")}_comparison_histogram.png`;
														a.href = pngUrl;
														a.click();
													};
													img.src = url;
												}}
											>
												Download
											</div>
										</div>

										<Form className="mt-4 w-25">
											<Form.Group className="mb-3">
												<Form.Label>Number of Bins</Form.Label>
												<Form.Control
													type="number"
													value={numberOfBins}
													onChange={(event) => {
														const value = event.target.value;
														setNumberOfBins(value === "" ? 67 : parseInt(value, 10));
													}}
													placeholder={numberOfBins.toLocaleString()}
												/>
											</Form.Group>
										</Form>

										<svg
											ref={chartRef}
											width="100%"
											height={containerRef.current ? containerRef.current.clientWidth * 0.6 : 600}
											viewBox={`0 0 ${containerRef.current?.clientWidth ?? 1200} ${containerRef.current?.clientWidth ? containerRef.current.clientWidth * 0.6 : 600}`}
											preserveAspectRatio="xMidYMid meet"
										/>
										
										<div style={{ marginTop: "12px", fontWeight: "bold", fontSize: "16px", textAlign: "center" }} />
										<div ref={legendRef} style={{ marginTop: "12px", display: "flex", flexWrap: "wrap" }} />
									</div>
								</div>
							</Card.Body>
						</Card>
					</Col>
				</Row>	

				{/* Tabulator Table */}
        <Row className="row py-4 mx-auto">
					<Col xs={12}>
						<Card bg="white">
							<Card.Header style={{ backgroundColor: "#2e8b8b"}} className="text-white">
								<h3 className="text-white">Data</h3>
							</Card.Header>
							<Card.Body>
                  <div className="mb-2 d-flex gap-2">
                    <button className="btn btn-secondary" onClick={() => handleDownload("csv")}>CSV</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("xlsx")}>Excel</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("html")}>HTML</button>
                    <button className="btn btn-secondary" onClick={() => handleDownload("json")}>JSON</button>
                  </div>
                  <div style={{ width: "100%",
                    position: "relative"
                  }}>
                    {/* Loading */}
                    {loading && (
                      <div className="text-center py-5">
                        <div className="text-primary" role="status"></div>
                        {/* Loader */}
                        {loading ? <Card.Text><Loader /></Card.Text> : <></>}
                      </div>
                    )}

										{/* Updated - No Data Available */}
										{!loading && (!masterData || masterData.length === 0) && (
											<Card.Text className="text-center py-5">No data available - please select a job title</Card.Text>
										)}

                    {/* Table Container - Rendered but hidden if not needed */}
                    <div 
                      ref={tableRef} 
											className="table-light"
                      style={{ 
												minHeight: 400,
												opacity: (!loading && (masterData?.length ?? 0) > 0) ? 1 : 0,
												height: (!loading && (masterData?.length ?? 0) > 0) ? "auto" : 0,
    										overflow: "hidden"
											}}  
                    />
                  </div>
                </Card.Body>
						</Card>
					</Col>
        </Row>
      </Container>
			<FooterSection />
    </>
  )
}

export default EntityCompareTwoEntities;